import React from 'react';

/**
 * Reusable input field for the Call Details Form.
 *
 * Why this exists: the original implementation defined this inside the parent
 * render function, which forced React to remount every input on each keystroke
 * (losing focus + breaking auto-save heuristics). Hoisting it here makes
 * inputs stable across renders.
 */
const formatPhoneNumber = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const formatCardNumber = (value, cardType = 'visa') => {
  const cleaned = value.replace(/\D/g, '');
  const maxLength = cardType === 'amex' ? 15 : 16;
  const truncated = cleaned.slice(0, maxLength);

  if (cardType === 'amex') {
    if (truncated.length <= 4) return truncated;
    if (truncated.length <= 10) return `${truncated.slice(0, 4)} ${truncated.slice(4)}`;
    return `${truncated.slice(0, 4)} ${truncated.slice(4, 10)} ${truncated.slice(10)}`;
  }
  const groups = truncated.match(/.{1,4}/g);
  return groups ? groups.join(' ') : truncated;
};

export const CallDetailsInputField = ({
  label,
  field,
  type = 'text',
  placeholder,
  required,
  icon: Icon,
  format,
  maxLength,
  value,
  error,
  cardType,
  onChange,
}) => (
  <div>
    <label className="block text-gray-400 text-sm mb-1.5">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />}
      <input
        type={type}
        value={value || ''}
        onChange={(e) => {
          let nextValue = e.target.value;
          if (format === 'phone') nextValue = formatPhoneNumber(nextValue);
          if (format === 'card') nextValue = formatCardNumber(nextValue, cardType);
          onChange(field, nextValue);
        }}
        maxLength={maxLength}
        placeholder={placeholder}
        data-testid={`call-details-input-${field}`}
        className={`w-full bg-[#1a1a1f] border rounded-lg px-4 py-2.5 text-white placeholder-gray-600 outline-none transition-colors ${
          Icon ? 'pl-10' : ''
        } ${error ? 'border-red-500' : 'border-gray-700 focus:border-teal-400'}`}
      />
    </div>
    {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
  </div>
);
